/**
 * Audit-console helpers shared by list views, detail drawers, and restore flows.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-27-v1
 */
import type {
    AdminAuditEntry,
    AuditDiffRow,
    AuditQueryFilters,
    AuditSeverity,
    AuditSource
} from './types';

const FILTER_ORDER: (keyof AuditQueryFilters)[] = [
    'from',
    'to',
    'severity',
    'q',
    'source',
    'resource_type',
    'resource_id',
    'actor_user_id',
    'restorable',
    'limit',
    'cursor'
];

const SENSITIVE_FIELD_RE = /(password|token|secret|api[_-]?key|bearer|cookie|authorization)/i;

export function buildAuditQueryString(filters: AuditQueryFilters = {}): string {
    const params = new URLSearchParams();

    for (const key of FILTER_ORDER) {
        const value = filters[key];
        if (value === undefined || value === null || value === '' || value === 'all') continue;
        params.set(key, String(value));
    }

    const qs = params.toString();
    return qs ? `?${qs}` : '';
}

export function getAuditDiffRows(entry: Pick<AdminAuditEntry, 'state_before' | 'state_after'>) {
    const before = entry.state_before ?? {};
    const after = entry.state_after ?? {};
    const keys = Array.from(new Set([...Object.keys(before), ...Object.keys(after)])).sort();

    return keys
        .filter((key) => stringifyValue(before[key]) !== stringifyValue(after[key]))
        .map<AuditDiffRow>((key) => ({
            field: key,
            before: formatDiffValue(key, before[key]),
            after: formatDiffValue(key, after[key])
        }));
}

export function groupAuditEntriesByDay<T extends AdminAuditEntry>(rows: T[]) {
    const formatter = new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });

    const sorted = [...rows].sort((a, b) => getEntryTime(b) - getEntryTime(a));
    const groups: { day: string; rows: T[] }[] = [];

    for (const row of sorted) {
        const day = formatter.format(new Date(getEntryTime(row)));
        const current = groups[groups.length - 1];
        if (current?.day === day) {
            current.rows.push(row);
        } else {
            groups.push({ day, rows: [row] });
        }
    }

    return groups;
}

export function buildCloudflareLogsQuery({
    request_id,
    cf_ray
}: Pick<AdminAuditEntry, 'request_id' | 'cf_ray'>): string {
    return [
        request_id ? `request_id:"${escapeLogsQueryValue(request_id)}"` : null,
        cf_ray ? `cf_ray:"${escapeLogsQueryValue(cf_ray)}"` : null
    ]
        .filter(Boolean)
        .join(' OR ');
}

export function getAuditSource(entry: Partial<AdminAuditEntry>): AuditSource {
    return entry.source === 'action' ? 'action' : 'event';
}

export function getAuditSeverity(entry: Partial<AdminAuditEntry>): AuditSeverity {
    if (entry.severity === 'destructive' || entry.severity === 'sensitive') return entry.severity;
    return 'routine';
}

export function getAuditTargetId(entry: AdminAuditEntry): string {
    return entry.resource_id || entry.target_id;
}

function getEntryTime(entry: AdminAuditEntry): number {
    const raw = entry.occurred_at || entry.ts;
    const direct = new Date(raw).getTime();
    if (Number.isFinite(direct)) return direct;

    const withSeconds = new Date(`${entry.ts.replace(' ', 'T')}:00`).getTime();
    return Number.isFinite(withSeconds) ? withSeconds : 0;
}

function formatDiffValue(field: string, value: unknown): string {
    if (SENSITIVE_FIELD_RE.test(field)) return '[redacted]';
    return stringifyValue(value);
}

function stringifyValue(value: unknown): string {
    if (value === undefined) return '';
    if (value === null) return 'null';
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    return JSON.stringify(value);
}

function escapeLogsQueryValue(value: string): string {
    return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}
